import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { ElementRenderer } from './2D/ElementRenderer'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store'
import { ElementConverter } from './2D/ElementConverter'
import {
  Beam2D,
  ElementsData,
  Header2D,
  Joist2D,
  Span2D,
  Storey,
  Wall2D,
  Window2D,
} from '../../models/Construction'
import { BaseElement } from './2D/Elements'
import { setConvertData } from '../../actions/construction'

const Viewer2D: React.FC = () => {
  const [scale, setScale] = useState<number>(0.7)
  const [thicknessMultiplier, setThicknessMultiplier] = useState<number>(1.5) // New state for thickness control
  const [selectedElement, setSelectedElement] = useState<{
    element: any
    type: string
  } | null>(null)

  const dispatch = useDispatch();
  const [updatedElementsByType,setUpdatedElementsByType] = useState<Record<string,Record<string,any>>>()

  const convertData = useSelector((state:RootState) => state.construction.convertData); 
  const selectedStoreyId = useSelector((state: RootState) => state.construction.selectedStoreyId);
  
  const storeyData = useMemo(() => {
      if(selectedStoreyId === null)
        return convertData.building.storeys
      return convertData.building.storeys.filter(storey => storey.id === selectedStoreyId)
    },
    [convertData.building.storeys, selectedStoreyId]
  );

  const convertedFloorPlanData: ElementsData | null = useMemo(
    () =>
      storeyData
        ? ElementConverter.convertStoreyDataToElementsData(storeyData)
        : null,
    [storeyData],
  )

  useEffect(() => {}, [
    selectedStoreyId,
    convertData,
    storeyData,
    convertedFloorPlanData,
  ])

  const [hiddenTypes, setHiddenTypes] = useState<Set<string>>(new Set())
  const [buildingData, setBuildingData] = useState<ElementsData | null>(
    convertedFloorPlanData,
  )
  const [highlightedType, setHighlightedType] = useState<string | null>(null)

  useEffect(() => {
    if (convertedFloorPlanData) {
      setBuildingData(convertedFloorPlanData)
    }
  }, [convertedFloorPlanData])

  const handleElementSelect = useCallback((element: any, type: string) => {
    setSelectedElement({ element, type })
  }, [])

  const handleElementDelete = useCallback((elementId: string, type: string) => {
    setBuildingData(prev => {
      if (!prev) return prev;
      const newData: ElementsData = {
        ...prev,
        rooms: prev.rooms ? [...prev.rooms] : [],
        walls: prev.walls ? [...prev.walls] : [],
        beams: prev.beams ? [...prev.beams] : [],
        strips: prev.strips ? [...prev.strips] : [],
      };

      switch (type) {
        case 'room':
          newData.rooms = newData.rooms?.filter(room => room.id !== elementId);
          break;
        case 'wall':
          newData.walls = newData.walls?.filter(wall => wall.id !== elementId);
          break;
        case 'beam':
          newData.beams = newData.beams?.filter(beam => beam.id !== elementId);
          break
        case 'header':
          newData.walls = newData.walls?.map(wall => {
            if (!wall.windows) return wall;
            return {
              ...wall,
              windows: wall.windows.map(window => {
                if (!window.headers) return window;
                return {
                  ...window,
                  headers: window.headers.filter(header => header?.id !== elementId)
                };
              })
            };
          });
          break;
      }

      return newData;
    });
    // Clear selection if deleted element was selected
    if (selectedElement && selectedElement.element.id === elementId) {
      setSelectedElement(null);
    }
  }, [selectedElement]);

  const handleElementUpdate = useCallback((elementId: string, elementType: string, updates: any) => {
    setBuildingData(prev => {
      const newData: ElementsData = JSON.parse(JSON.stringify(prev));
      
      switch (elementType.toLowerCase()) {
        case 'wall':
          const wallIndex = newData.walls?.findIndex((w: Wall2D) => w.id === elementId);
          if (wallIndex !== undefined && wallIndex >= 0 && newData.walls) {
            const updatedElement = { ...newData.walls[wallIndex], ...updates }
            newData.walls[wallIndex] = updatedElement;
            updateModifiedElements(elementType,updatedElement)
          }
          break;
        case 'beam':
          const beamIndex = newData.beams?.findIndex((b: Beam2D) => b.id  === elementId);
          if (beamIndex !== undefined && beamIndex >=0 && newData.beams) {
            const updatedElement = { ...newData.beams[beamIndex], ...updates };
            updateModifiedElements(elementType,updatedElement)
          }
          break;
        case 'header':
          const walls = newData.walls;
          walls?.forEach((wall: Wall2D) => {
            wall.windows?.forEach((window: Window2D) => {
                const headerIndex = window.headers?.findIndex((h: Header2D) => h.id  === elementId);
                if( headerIndex !== undefined && headerIndex >=0 && window.headers ){
                  const updatedData = {...window.headers[headerIndex], ...updates}
                  window.headers[headerIndex] = updatedData;
                  updateModifiedElements(elementType,updatedData)
                }
            })
          })
          break; 
        case 'joist':
          newData.spans?.forEach((span: Span2D) => {
          const joistIndex = span.joists?.findIndex((j: Joist2D) => j.id  === elementId);
          if( joistIndex !== undefined && joistIndex >=0 && span.joists ){
            const updatedData = {...span.joists[joistIndex], ...updates}
            span.joists[joistIndex] = updatedData;
          }
          })
          break;          
      }

      return newData;
    });
    
    // if (selectedElement?.element.id === elementId) {
    //   setSelectedElement((prev) => {
    //     if (!prev) return null
    //     return {
    //       ...prev,
    //       element: { ...prev.element, ...updates },
    //     }
    //   })
    // }
  },[selectedElement]);

  const updateModifiedElements = (elementType: string, element: BaseElement) => {
      const exisitngUpdatedElements = updatedElementsByType?.[elementType];
      setUpdatedElementsByType({...updatedElementsByType,[elementType]:{...exisitngUpdatedElements,[element.id]:element}})
  }

  useEffect(() => {
  if (updatedElementsByType && convertData) {
    const updatedConvertData = JSON.parse(JSON.stringify(convertData));
    
    updatedConvertData.building.storeys.forEach((storey: Storey) => {
      if (storey.id === selectedStoreyId) {
        Object.keys(updatedElementsByType).forEach(elementType => {
          if (elementType.toLowerCase() === 'beam') {
            const updatedElements = updatedElementsByType[elementType];
            storey.beams.forEach((beam, index) => {
              if (updatedElements[beam.id]) {
                storey.beams[index] = {
                  ...storey.beams[index],
                  ...updatedElements[beam.id]
                };
              }
            });
          }
        });
      }
    });

    dispatch(setConvertData(updatedConvertData));
  }
}, [updatedElementsByType]);
  
  return (
    <div className='p-6 bg-gray-50 min-h-screen'>
      <div className='flex gap-6'>
        <div className='flex-1'>
          <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem' }}>
            <ElementRenderer
              elementsData={buildingData}
              scale={scale}
              thicknessMultiplier={thicknessMultiplier} // Pass thickness multiplier
              selectedElement={selectedElement}
              onElementSelect={handleElementSelect}
              onElementDelete={handleElementDelete}
              onElementUpdate={handleElementUpdate}
              highlightedType={highlightedType}
              hiddenTypes={hiddenTypes}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default Viewer2D
